import { Box, Typography } from "@mui/material";
import { useState } from "react";

import logo from "../../assets/branding/logo.svg";

export type LogoVariant = "default" | "white" | "dark" | "icon";

export interface LogoProps {
  variant?: LogoVariant;
  responsiveToDarkMode?: boolean;
  height?: number;
  ariaLabel?: string;
  className?: string;
}

const LOGO_SRC: Record<LogoVariant, string> = {
  default: logo,
  white: logo,
  dark: logo,
  icon: logo,
};

function LogoImage({
  src,
  height,
  width,
  ariaLabel,
  onError,
}: {
  src: string;
  height: number;
  width?: number;
  ariaLabel?: string;
  onError?: () => void;
}) {
  return (
    <img
      src={src}
      alt={ariaLabel ? "" : undefined}
      aria-label={ariaLabel}
      height={height}
      width={width}
      onError={onError}
      style={{
        display: "block",
        objectFit: "contain",
        minHeight: height,
        maxWidth: "100%",
      }}
    />
  );
}

export default function Logo({
  variant = "default",
  responsiveToDarkMode = false,
  height = 32,
  ariaLabel = "Tranos",
  className,
}: LogoProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const width = height;

  const handleError = () => setImgFailed(true);

  if (imgFailed) {
    return (
      <Typography
        component="span"
        variant="h6"
        sx={{ fontWeight: 700, color: "primary.main" }}
        className={className}
        aria-label={ariaLabel}
      >
        Tranos
      </Typography>
    );
  }

  if (responsiveToDarkMode) {
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", minHeight: height }}
        className={className}
        aria-hidden
      >
        <Box
          component="span"
          sx={{
            display: "block",
            "@media (prefers-color-scheme: dark)": { display: "none" },
          }}
        >
          <LogoImage src={logo} height={height} width={width} onError={handleError} />
        </Box>
        <Box
          component="span"
          sx={{
            display: "none",
            "@media (prefers-color-scheme: dark)": { display: "block" },
          }}
        >
          <LogoImage src={logo} height={height} width={width} onError={handleError} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", minHeight: height }}
      className={className}
      aria-hidden={!ariaLabel}
    >
      <LogoImage
        src={LOGO_SRC[variant]}
        height={height}
        width={width}
        ariaLabel={ariaLabel}
        onError={handleError}
      />
    </Box>
  );
}
